import React, { useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { COLORS } from "@/react/theme/constants";
import { ArrowUpIcon } from "@8x8/oxygen-icon";
import ArrowDownIcon from "@8x8/oxygen-icon/icons/ArrowDownIcon";
import { useTrunkGatewayContext } from "@/react/features/support-tools/siptrunk/TrunkGatewayContext";
import { PROTOCOL } from "@/react/features/support-tools/siptrunk/constants";
import LabeledRadioGroup from "@/react/components/LabeledRadioGroup";
import ValidatedInput from "@/react/features/support-tools/siptrunk/components/ValidatedInput";
import schema from "./ipBoxSchema";
import { useFocusedField } from "@/react/features/support-tools/siptrunk/useFocusedField";
import { IconButton } from "@8x8/oxygen-icon-button";
import CloseCircleIcon from "@8x8/oxygen-icon/icons/CloseCircleIcon";

const Box = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownField = styled.div`
  cursor: pointer;
  background: ${COLORS.tertiary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  padding: 8px;
  border: 2px solid transparent;

  ${(props) =>
    props.hasError &&
    css`
      border-color: ${COLORS.danger};
    `}
`;

const ExpandedContent = styled.div`
  border: 1px solid ${COLORS.tertiary};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;

  & > *:first-child {
    grid-column: span 2;
  }

  & > * {
    align-self: center;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const buildPath = (boxType, boxIndex, property) => {
  return `${boxType}[${boxIndex}].${property}`;
};

const BOX_PROPS = Object.freeze({
  IP_ADDRESS: "ipAddress",
  PROTOCOL: "protocol",
  PRIORITY: "priority",
  WEIGHT: "weight",
  PORT: "port",
});

export const IpBox = ({
  index,
  boxType,
  ipAddress,
  protocol,
  priority,
  weight,
  port,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { updateField, setIsValid, removeIpBox } = useTrunkGatewayContext();
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const fields = useMemo(
    () => [
      {
        prop: BOX_PROPS.IP_ADDRESS,
        label: "IP Address",
        value: ipAddress,
        isRadio: false,
      },
      {
        prop: BOX_PROPS.PROTOCOL,
        label: "Protocol",
        value: protocol,
        isRadio: true,
      },
      {
        prop: BOX_PROPS.PRIORITY,
        label: "Priority",
        value: priority,
        isRadio: false,
      },
      {
        prop: BOX_PROPS.WEIGHT,
        label: "Weight",
        value: weight,
        isRadio: false,
      },
      {
        prop: BOX_PROPS.PORT,
        label: "Port",
        value: port,
        isRadio: false,
      },
    ],
    [ipAddress, protocol, priority, weight, port]
  );

  const validateField = (name, value) => {
    try {
      schema.validateSyncAt(name, { [name]: value });
      setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error.message }));
    }
  };

  // Trigger validations on component mount
  useEffect(() => {
    fields.forEach(
      (field) =>
        field.isRadio === false && validateField(field.prop, field.value)
    );
  }, []);

  const hasError = useMemo(
    () => Object.values(errors).some((err) => err),
    [errors]
  );

  useEffect(() => {
    setIsValid(!hasError);
  }, [hasError, setIsValid]);

  useFocusedField({ error: errors, focusedField });

  const handleOnChange = (e, property) => {
    const { name, value } = e.target;
    setFocusedField(name);
    updateField(name, value);
    validateField(property, value);
  };

  const handleRemoveBox = (e) => {
    e.stopPropagation();
    removeIpBox({ boxType, index });
  };

  return (
    <Box>
      <DropdownField
        onClick={() => setIsExpanded(!isExpanded)}
        hasError={hasError}
      >
        <span>{ipAddress || "IP Address"}</span>
        <IconsWrapper>
          {isExpanded ? <ArrowUpIcon size={20} /> : <ArrowDownIcon size={20} />}
          <IconButton onClick={handleRemoveBox}>
            <CloseCircleIcon />
          </IconButton>
        </IconsWrapper>
      </DropdownField>
      {isExpanded && (
        <ExpandedContent>
          {fields.map((field) =>
            field.isRadio === true ? (
              <LabeledRadioGroup
                prompt={field.label}
                name={buildPath(boxType, index, field.prop)}
                value={field.value}
                onChange={(value) =>
                  updateField(buildPath(boxType, index, field.prop), value)
                }
                options={[PROTOCOL.TCP, PROTOCOL.UDP]}
              />
            ) : (
              <ValidatedInput
                prompt={field.label}
                type={"text"}
                name={buildPath(boxType, index, field.prop)}
                placeholder={field.label}
                value={field.value}
                onChange={(e) => handleOnChange(e, field.prop)}
                error={errors[field.prop]}
              />
            )
          )}
        </ExpandedContent>
      )}
    </Box>
  );
};
